import React, { useMemo } from 'react';
import { Box, IconButton, Tooltip, Paper, Checkbox, FormControlLabel, Button } from '@mui/material';
import { Delete, Clear, Add } from '@mui/icons-material';
import { FieldSelector } from './FieldSelector';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { OperatorSelector } from './OperatorSelector';
import { ValueInput } from './ValueInput';
import { QueryExpression, EqualsQueryValue, RangeQueryValue, RegexpQueryValue, SizeQueryValue, InQueryItem } from '@/api/bol';
import type { QueryOperator } from '@/utils/query-builder';

const MAX_EQUALS_FIELDS = 10;

export interface ConditionBuilderProps {
  query: QueryExpression;
  onChange: (query: QueryExpression) => void;
  onRemove?: () => void;
  disabled?: boolean;
  showRemoveButton?: boolean;
  size?: 'small' | 'medium';
  logisticsObjectType?: LogisticsObjectScope;
}

// Default values for each operator type
const DEFAULT_VALUES: Record<QueryOperator, EqualsQueryValue | InQueryItem[] | RangeQueryValue | Record<string, unknown> | string | SizeQueryValue | RegexpQueryValue> = {
  equals: '' as EqualsQueryValue,
  in: [] as InQueryItem[],
  range: {} as RangeQueryValue,
  match: {},
  exists: '',
  size: 0 as SizeQueryValue,
  regexp: { value: '' } as RegexpQueryValue
};

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  query,
  onChange,
  onRemove,
  disabled = false,
  showRemoveButton = true,
  size = 'small',
  logisticsObjectType = 'all'
}) => {
  const isNegated = query.not !== undefined;
  const baseQuery = useMemo(() => 
    isNegated ? (query.not || {}) : query,
    [isNegated, query]
  );

  // Extract operator, field, and value from query
  const { operator, field, value } = useMemo(() => {
    if (baseQuery.equals) {
      const field = Object.keys(baseQuery.equals)[0];
      return { operator: 'equals' as QueryOperator, field, value: baseQuery.equals[field] };
    }
    if (baseQuery.in) {
      const field = Object.keys(baseQuery.in)[0];
      return { operator: 'in' as QueryOperator, field, value: baseQuery.in[field] };
    }
    if (baseQuery.range) {
      const field = Object.keys(baseQuery.range)[0];
      return { operator: 'range' as QueryOperator, field, value: baseQuery.range[field] };
    }
    if (baseQuery.match) {
      const field = Object.keys(baseQuery.match)[0];
      return { operator: 'match' as QueryOperator, field, value: baseQuery.match[field] };
    }
    if (baseQuery.exists !== undefined) {
      return { operator: 'exists' as QueryOperator, field: baseQuery.exists, value: undefined };
    }
    if (baseQuery.size) {
      const field = Object.keys(baseQuery.size)[0];
      return { operator: 'size' as QueryOperator, field, value: baseQuery.size[field] };
    }
    if (baseQuery.regexp) {
      const field = Object.keys(baseQuery.regexp)[0];
      return { operator: 'regexp' as QueryOperator, field, value: baseQuery.regexp[field] };
    }
    return { operator: undefined, field: undefined, value: undefined };
  }, [baseQuery]);

  // Get equals fields for multi-field equals mode
  const equalsFields = useMemo(() => {
    if (!baseQuery.equals) return undefined;
    const fields = Object.keys(baseQuery.equals);
    if (fields.length <= 1) return undefined;
    return fields.map(field => ({ field, value: baseQuery.equals![field] }));
  }, [baseQuery.equals]);

  // Check if we can add more equals fields (max 10, no empty fields)
  const canAddEqualsField = useMemo(() => {
    if (!baseQuery.equals) return false;
    const fields = Object.keys(baseQuery.equals);
    const hasEmptyField = fields.some(field => field === '');
    return fields.length < MAX_EQUALS_FIELDS && !hasEmptyField;
  }, [baseQuery.equals]);

  // Helper to wrap/unwrap query with NOT operator
  const wrapQuery = (newQuery: QueryExpression): QueryExpression => {
    return isNegated ? { not: newQuery } : newQuery;
  };

  // Create query for given operator and field
  const createQueryForOperator = (op: QueryOperator, fieldName: string, val?: unknown): QueryExpression => {
    const queryValue = val ?? DEFAULT_VALUES[op];
    
    if (op === 'exists') {
      return { exists: fieldName };
    }
    
    return { [op]: { [fieldName]: queryValue } } as QueryExpression;
  };

  const handleFieldChange = (newField: string | null) => {
    if (!newField || !operator) return;
    onChange(wrapQuery(createQueryForOperator(operator, newField, value)));
  };

  const handleOperatorChange = (newOperator: string | null) => {
    if (!newOperator) return;
    onChange(wrapQuery(createQueryForOperator(newOperator as QueryOperator, field || '', null)));
  };

  const handleValueChange = (newValue: unknown) => {
    if (!operator || !field) return;
    onChange(wrapQuery(createQueryForOperator(operator, field, newValue)));
  };

  const handleAddEqualsField = () => {
    if (!baseQuery.equals) return;
    
    // Check if there are any empty fields before adding a new one
    const hasEmptyField = Object.keys(baseQuery.equals).some(field => field === '');
    if (hasEmptyField) return;
    
    const newEquals = { ...baseQuery.equals, '': '' as EqualsQueryValue };
    onChange(wrapQuery({ equals: newEquals }));
  };

  const handleRemoveEqualsField = (fieldToRemove: string) => {
    if (!baseQuery.equals) return;
    
    const newEquals = { ...baseQuery.equals };
    delete newEquals[fieldToRemove];
    
    if (Object.keys(newEquals).length === 0) {
      onChange({});
    } else {
      onChange(wrapQuery({ equals: newEquals }));
    }
  };

  const handleEqualsFieldChange = (oldField: string, newField: string) => {
    if (!baseQuery.equals) return;
    
    const value = baseQuery.equals[oldField];
    const newEquals = { ...baseQuery.equals };
    delete newEquals[oldField];
    newEquals[newField] = value;
    
    onChange(wrapQuery({ equals: newEquals }));
  };

  const handleEqualsValueChange = (fieldName: string, stringValue: string) => {
    if (!baseQuery.equals) return;
    
    // Parse value as number or boolean if applicable
    let parsedValue: EqualsQueryValue = stringValue;
    if (stringValue === 'true') parsedValue = true;
    else if (stringValue === 'false') parsedValue = false;
    else if (stringValue !== '' && !Number.isNaN(Number(stringValue))) parsedValue = Number(stringValue);
    
    onChange(wrapQuery({ equals: { ...baseQuery.equals, [fieldName]: parsedValue } }));
  };

  const handleClear = () => onChange({});

  const handleNegateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked ? { not: baseQuery } : baseQuery);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 1.5, 
        border: 1, 
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: isNegated ? 'error.50' : 'background.paper',
        borderLeftWidth: 3,
        borderLeftColor: isNegated ? 'error.main' : 'divider'
      }}
    >
      {/* First Row: Operator | NOT checkbox | Clear | Remove */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ flex: '1 1 auto' }}>
          <OperatorSelector
            value={operator}
            onChange={handleOperatorChange}
            disabled={disabled}
            size={size}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={isNegated}
              onChange={handleNegateChange}
              disabled={disabled}
              size={size}
              color="error"
            />
          }
          label="NOT"
          sx={{ 
            mr: 0, 
            flexShrink: 0,
            '& .MuiFormControlLabel-label': { 
              fontSize: '0.875rem', 
              fontWeight: isNegated ? 600 : 400 
            } 
          }}
        />

        <Tooltip title="Clear condition">
          <span>
            <IconButton 
              onClick={handleClear} 
              disabled={disabled}
              size={size}
            >
              <Clear fontSize={size} />
            </IconButton>
          </span>
        </Tooltip>

        {showRemoveButton && onRemove && (
          <Tooltip title="Remove condition">
            <span>
              <IconButton 
                onClick={onRemove} 
                disabled={disabled}
                size={size}
                color="error"
              >
                <Delete fontSize={size} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Field and Value inputs */}
      {operator && operator !== 'exists' && (
        <Box>
          {operator === 'equals' && equalsFields ? (
            // Multi-field equals mode
            <>
              {equalsFields.map((fieldPair, index) => (
                <Paper
                  key={fieldPair.field || index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: index < equalsFields.length - 1 ? 1.5 : 0,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>
                    <FieldSelector
                      value={fieldPair.field}
                      onChange={(newField) => handleEqualsFieldChange(fieldPair.field, newField || '')}
                      disabled={disabled}
                      size={size}
                      logisticsObjectType={logisticsObjectType}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <ValueInput
                        operator="equals"
                        value={fieldPair.value}
                        onChange={(newValue) => handleEqualsValueChange(fieldPair.field, String(newValue))}
                        disabled={disabled}
                        size={size}
                      />
                    </Box>
                    <Tooltip title="Remove field">
                      <span>
                        <IconButton
                          onClick={() => handleRemoveEqualsField(fieldPair.field)}
                          disabled={disabled || equalsFields.length === 1}
                          size={size}
                          color="error"
                          sx={{ mt: 0.5 }}
                        >
                          <Delete fontSize={size} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
              
              <Box sx={{ mt: 1.5 }}>
                <Button
                  startIcon={<Add />}
                  onClick={handleAddEqualsField}
                  disabled={disabled || !canAddEqualsField}
                  size={size}
                  variant="outlined"
                >
                  Add Another Field
                </Button>
              </Box>
            </>
          ) : (
            // Standard single field-value layout
            <>
              <Box sx={{ mb: 1.5 }}>
                <FieldSelector
                  value={field}
                  onChange={handleFieldChange}
                  disabled={disabled}
                  size={size}
                  logisticsObjectType={logisticsObjectType}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <ValueInput
                    operator={operator}
                    value={value}
                    onChange={handleValueChange}
                    disabled={disabled}
                    size={size}
                  />
                </Box>
              </Box>
              
              {operator === 'equals' && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    startIcon={<Add />}
                    onClick={handleAddEqualsField}
                    disabled={disabled || (field === '' || field === undefined)}
                    size={size}
                    variant="outlined"
                  >
                    Add Another Field
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Exists operator only needs field */}
      {operator === 'exists' && (
        <Box>
          <FieldSelector
            value={field}
            onChange={handleFieldChange}
            disabled={disabled}
            size={size}
            logisticsObjectType={logisticsObjectType}
          />
        </Box>
      )}
    </Paper>
  );
};
